import React, { useState, useEffect } from 'react';
import { FileText, Download, RefreshCw, Database, Calculator, Upload, AlertTriangle, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import API from '../services/apiBase';
import { useClient } from '../context/ClientContext';
import { getScheduleIColumnsForLayout, SCHEDULE_I_COLUMNS } from './scheduleIHeaders';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';

const headerCategory = (label) => {
  const l = (label || '').toLowerCase();
  if (
    l.includes('internal contract') ||
    l.includes('project number') ||
    l.includes('prime contract') ||
    l.includes('sub contract') ||
    l.includes('contract type') ||
    l.includes('prime or sub') ||
    l.includes('dod or non') ||
    l.includes('start year') ||
    l.includes('physically complete')
  ) return 'info';

  if (l.includes('direct labor') || l.includes('total direct labor')) return 'labor';
  if (l.includes('fringe')) return 'fringe';
  if (l.includes(' oh') || l.includes('overhead')) return 'overhead';
  if (l.includes('odc') || l.includes('subcontractor') || l.includes('material')) return 'odc';
  if (l.includes('sm&h') || l.includes('g&a')) return 'indirect';
  if (l.includes('unbillable') || l.includes('less ')) return 'unbillable';
  if (l.includes('settled') || l.includes('claimed') || l.includes('cost')) return 'costs';
  if (l.includes('billing') || l.includes('billed') || l.includes('voucher')) return 'billing';
  if (l.includes('over') || l.includes('under') || l.includes('total contract')) return 'grand_total';
  if (l.includes('ceiling') || l.includes('funding') || l.includes('limitation')) return 'adjustment';

  return 'default';
};

const headerBg = (category) => {
  switch (category) {
    case 'info': return 'var(--gray-700)';
    case 'labor': return 'var(--primary-blue-dark)';
    case 'fringe': return 'var(--success-green)';
    case 'overhead': return 'var(--secondary-blue)';
    case 'odc': return 'var(--accent-orange)';
    case 'indirect': return 'var(--warning-amber)';
    case 'costs': return '#6366f1';
    case 'unbillable': return '#dc2626';
    case 'billing': return '#0891b2';
    case 'adjustment': return '#7c3aed';
    case 'grand_total': return 'var(--gray-900)';
    default: return 'var(--gray-700)';
  }
};

const ScheduleIPage = () => {
  const { clientId, clientName, hasActiveClient } = useClient();
  // Data sources
  const [recentScheduleH, setRecentScheduleH] = useState([]);
  const [selectedScheduleHId, setSelectedScheduleHId] = useState('');
  const [billingFiles, setBillingFiles] = useState([]);
  const [selectedBillingId, setSelectedBillingId] = useState('');
  // Claim cutoff for billing aggregation (Date Costs Billed Through target).
  // Required by the backend; there is deliberately no default.
  const [fiscalYearEnd, setFiscalYearEnd] = useState('');

  // Prior-year Schedule I template (carry-forward source)
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [noPriorTemplate, setNoPriorTemplate] = useState(false);
  
  // Schedule I calculations
  const [recentScheduleI, setRecentScheduleI] = useState([]);
  const [scheduleIData, setScheduleIData] = useState(null);
  const [selectedScheduleIId, setSelectedScheduleIId] = useState('');
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [viewLimit, setViewLimit] = useState(500);
  
  // Schema columns from onboarding template
  const [schemaColumns, setSchemaColumns] = useState(null);

  // Filters
  const [contractSearch, setContractSearch] = useState('');
  const [hasDataOnly, setHasDataOnly] = useState(false);
  const [sortBy, setSortBy] = useState('internal_contract_number');
  const [sortOrder, setSortOrder] = useState('asc');

  // Load Schedule I schema (onboarding labels) when client changes
  useEffect(() => {
    if (!clientId) return;
    const loadSchema = async () => {
      try {
        const schemaUrl = `${API}/schedule-i/schema?client_id=${clientId}`;
        const res = await fetch(schemaUrl);
        if (res.ok) {
          const data = await res.json();
          const cols = Array.isArray(data?.columns)
            ? data.columns.filter((c) => c && c.key && c.label)
            : [];
          if (cols.length > 0) {
            setSchemaColumns(cols);
          }
        }
      } catch (e) {
        console.warn('Failed to load Schedule I schema', e);
      }
    };
    loadSchema();
  }, [clientId]);

  // Load data on mount and client change
  useEffect(() => {
    if (clientId) {
      loadRecentScheduleH();
      loadBillingFiles();
      loadTemplates();
      loadRecentScheduleI();
    }
  }, [clientId]);

  const loadRecentScheduleH = async () => {
    if (!clientId) return;
    try {
      const res = await fetch(`${API}/schedule-h/list?tenant_id=${clientId}`);
      if (res.ok) {
        const data = await res.json();
        setRecentScheduleH(data.calculations || []);
      }
    } catch (e) {
      console.warn('Failed to load Schedule H list', e);
    }
  };

  const loadBillingFiles = async () => {
    if (!clientId) return;
    try {
      const res = await fetch(`${API}/schedule-i/billing/list?tenant_id=${clientId}`);
      if (res.ok) {
        const data = await res.json();
        setBillingFiles(data.billing_files || []);
      }
    } catch (e) {
      console.warn('Failed to load billing files', e);
    }
  };

  const loadRecentScheduleI = async () => {
    try {
      const res = await fetch(`${API}/schedule-i/list`);
      if (res.ok) {
        const data = await res.json();
        setRecentScheduleI(data.calculations || []);
      }
    } catch (e) {
      console.warn('Failed to load Schedule I list', e);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API}/schedule-i/template/list`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (e) {
      console.warn('Failed to load Schedule I templates', e);
    }
  };

  const handleTemplateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingTemplate(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/schedule-i/upload-template`, {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        const warn = (data.warnings || []).length
          ? ` Warnings: ${data.warnings.join(' ')}`
          : '';
        setSuccess(
          `Parsed ${data.contract_count.toLocaleString()} prior-year contract rows from "${data.filename}". ` +
          `Settled $${Math.round(data.totals?.settled_costs || 0).toLocaleString()}, ` +
          `funding limitation $${Math.round(data.totals?.funding_limitation || 0).toLocaleString()}. ` +
          `Check these against the source file before relying on them.${warn}`
        );
        setSelectedTemplateId(data.template_id);
        setNoPriorTemplate(false);
        await loadTemplates();
      } else {
        const errorData = await res.json();
        setError(errorData.detail || 'Failed to upload Schedule I template');
      }
    } catch (e) {
      setError(`Template upload error: ${e.message}`);
    } finally {
      setUploadingTemplate(false);
      e.target.value = '';
    }
  };

  const handleBillingUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!fiscalYearEnd) {
      setError('Set the fiscal year end date first: billing aggregation needs the claim cutoff (Date Costs Billed Through target).');
      e.target.value = '';
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fiscal_year_end', fiscalYearEnd);

      const res = await fetch(`${API}/schedule-i/upload-billing`, {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setSuccess(`Uploaded ${data.row_count.toLocaleString()} billing records for ${data.project_count.toLocaleString()} projects`);
        setSelectedBillingId(data.billing_id);
        await loadBillingFiles();
      } else {
        const errorData = await res.json();
        setError(errorData.detail || 'Failed to upload billing file');
      }
    } catch (e) {
      setError(`Upload error: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleCalculate = async () => {
    if (!selectedScheduleHId) {
      setError('Please select a Schedule H dataset');
      return;
    }

    setCalculating(true);
    setError(null);

    try {
      const res = await fetch(`${API}/schedule-i/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schedule_h_id: selectedScheduleHId,
          billing_id: selectedBillingId || null,
          contract_filter: contractSearch || null,
          template_id: selectedTemplateId || null,
          no_prior_template: noPriorTemplate
        })
      });

      if (res.ok) {
        const data = await res.json();
        setScheduleIData(data);
        setSelectedScheduleIId(data.schedule_i_id);
        await loadRecentScheduleI();
        const method = data.calculation_method === 'schedule_i_typed_executor'
          ? `client rules engine, ${data.contracts_with_template_row ?? 0} of ${data.contract_count} contracts matched the prior-year template`
          : 'legacy calculation';
        setSuccess(`Calculated Schedule I for ${data.contract_count} contracts (${method})`);
      } else {
        const errorData = await res.json();
        setError(errorData.detail || 'Failed to calculate Schedule I');
      }
    } catch (e) {
      setError(`Calculation error: ${e.message}`);
    } finally {
      setCalculating(false);
    }
  };

  const loadScheduleI = async (scheduleIId) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (contractSearch) params.append('contract_search', contractSearch);
      if (hasDataOnly) params.append('has_data_only', 'true');
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);

      const url = `${API}/schedule-i/${scheduleIId}${params.toString() ? '?' + params.toString() : ''}`;
      const res = await fetch(url);

      if (res.ok) {
        const data = await res.json();
        setScheduleIData(data);
        setSelectedScheduleIId(scheduleIId);
      } else {
        setError('Failed to load Schedule I data');
      }
    } catch (e) {
      setError(`Load error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (selectedScheduleIId) {
      loadScheduleI(selectedScheduleIId);
    }
  };

  const handleExport = () => {
    if (!scheduleIData || !scheduleIData.schedule_i_id) {
      setError('No Schedule I data to export');
      return;
    }
    const url = `${API}/schedule-i/${scheduleIData.schedule_i_id}/export`;
    setError(null);
    // Use fetch with auth header instead of window.open (which drops the token)
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Export failed (${res.status}): ${text}`);
        }
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = blobUrl;
        a.download = `Schedule_I_${scheduleIData.schedule_i_id.slice(0, 8)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        }, 200);
      } catch (err) {
        console.error('[Schedule I] Export error:', err);
        setError(err.message || 'Export failed');
      }
    })();
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '—';
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(value));
    if (value < 0) {
      return <span style={{ color: 'var(--error-red)' }}>({formatted})</span>;
    }
    return formatted;
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return value;
    }
  };

  const getColumnValue = (contract, column) => {
    return contract[column.key];
  };

  // Prefer onboarding template labels from schema, fall back to hardcoded
  const scheduleIColumns = schemaColumns || getScheduleIColumnsForLayout(scheduleIData?.layout);

  return (
    <div style={{ padding: '2rem', maxWidth: '100%', margin: '0 auto' }}>
      {/* Header */}
      <div className="reconcile-header" style={{ marginBottom: '2rem', padding: '1.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
        <div className="reconcile-title">Schedule I</div>
        <div className="reconcile-meta">
          <div className="reconcile-meta-item">
            <FileText style={{ width: '1rem', height: '1rem' }} />
            <span className="reconcile-meta-label">Module:</span>
            <span>Cumulative Indirect Costs & Billing Status</span>
          </div>
          <div className="reconcile-meta-item">
            <span className="reconcile-meta-label">Status:</span>
            <span className={`pro-badge ${scheduleIData ? 'pro-badge-success' : 'pro-badge-info'}`}>
              {scheduleIData ? 'Calculated' : 'Ready'}
            </span>
          </div>
        </div>
      </div>

      {/* Data Sources Card */}
      <div className="pro-card" style={{ marginBottom: '2rem' }}>
        <div className="pro-card-header">
          <h3 className="pro-card-title">
            <Database style={{ width: '1.25rem', height: '1.25rem', display: 'inline', marginRight: '0.5rem' }} />
            Data Sources
          </h3>
        </div>
        <div className="pro-card-content">
          {/* Schedule H Selection */}
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem' }}>1. Select Schedule H (Contract Costs)</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'end' }}>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                  Schedule H Dataset:
                </label>
                <select
                  value={selectedScheduleHId}
                  onChange={(e) => setSelectedScheduleHId(e.target.value)}
                  className="pro-input"
                  disabled={calculating}
                >
                  <option value="">-- Select Schedule H --</option>
                  {recentScheduleH.map((calc, idx) => (
                    <option key={idx} value={calc.schedule_h_id}>
                      {calc.contract_count} contracts • {calc.gl_id} • {new Date(calc.calculated_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Billing Data Upload */}
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem' }}>2. Upload or Select Billing Data</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                  Fiscal Year End (claim cutoff):
                </label>
                <input
                  type="date"
                  value={fiscalYearEnd}
                  onChange={(e) => setFiscalYearEnd(e.target.value)}
                  className="pro-input"
                  disabled={uploading}
                  title="Date Costs Billed Through target: voucher, billed-through date and cumulative billed are taken from the invoice closest to this date"
                />
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                  Upload New Billing File:
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleBillingUpload}
                  disabled={uploading}
                  style={{ fontSize: '0.875rem' }}
                />
                {uploading && <span style={{ marginLeft: '0.5rem', color: 'var(--primary-blue)' }}>Uploading...</span>}
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                  Or Select Existing:
                </label>
                <select
                  value={selectedBillingId}
                  onChange={(e) => setSelectedBillingId(e.target.value)}
                  className="pro-input"
                  disabled={calculating}
                >
                  <option value="">(No billing data - costs only)</option>
                  {billingFiles.map((bf, idx) => (
                    <option key={idx} value={bf.billing_id}>
                      {bf.filename} • {bf.project_count} projects • ${bf.total_billed?.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Prior-Year Schedule I Template Upload */}
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>3. Upload or Select Prior-Year Schedule I</h4>
            <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
              Source for the carry-forward columns: Settled Costs, Prior Year Unsettled, Prior Unbillables, Funding Limitation, Physically Complete.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                  Upload Prior-Year Template:
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleTemplateUpload}
                  disabled={uploadingTemplate}
                  style={{ fontSize: '0.875rem' }}
                />
                {uploadingTemplate && <span style={{ marginLeft: '0.5rem', color: 'var(--primary-blue)' }}>Parsing...</span>}
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                  Or Select Existing:
                </label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className="pro-input"
                  disabled={calculating || noPriorTemplate}
                >
                  <option value="">(Most recent upload)</option>
                  {templates.map((t, idx) => (
                    <option key={idx} value={t.template_id}>
                      {t.filename} • {t.contract_count?.toLocaleString()} contracts • {new Date(t.uploaded_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
                <label style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <input
                    type="checkbox"
                    checked={noPriorTemplate}
                    onChange={(e) => setNoPriorTemplate(e.target.checked)}
                    disabled={calculating}
                  />
                  This client has no prior-year Schedule I (first year)
                </label>
              </div>
            </div>
          </div>

          {/* Calculate Button */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button
              className="pro-btn pro-btn-primary"
              onClick={handleCalculate}
              disabled={calculating || !selectedScheduleHId}
              style={{ fontSize: '0.875rem', padding: '0.625rem 1.25rem' }}
            >
              {calculating ? (
                <>
                  <RefreshCw style={{ width: '1rem', height: '1rem', marginRight: '0.5rem', animation: 'spin 1s linear infinite' }} />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                  Calculate Schedule I
                </>
              )}
            </button>
            <span style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
              Links contract costs with billing records to show over/under billing
            </span>
          </div>

          {/* Load Previous */}
          {recentScheduleI.length > 0 && (
            <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem' }}>Or Load Previously Calculated</h4>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {recentScheduleI.slice(0, 5).map((calc, idx) => (
                  <div
                    key={idx}
                    onClick={() => loadScheduleI(calc.schedule_i_id)}
                    style={{
                      padding: '0.75rem',
                      background: selectedScheduleIId === calc.schedule_i_id ? 'var(--primary-blue)' : 'var(--white)',
                      color: selectedScheduleIId === calc.schedule_i_id ? 'var(--white)' : 'var(--gray-700)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      border: '1px solid var(--gray-200)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8125rem' }}>
                      <span style={{ fontWeight: '500' }}>
                        {calc.contract_count} contracts • Over: {calc.over_billed_count} • Under: {calc.under_billed_count}
                      </span>
                      <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                        {new Date(calc.calculated_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {error && (
            <Alert className="mt-4" variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="mt-4" style={{ background: '#dcfce7', border: '1px solid #16a34a' }}>
              <AlertDescription style={{ color: '#16a34a' }}>{success}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {scheduleIData && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Claimed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(scheduleIData.total_claimed)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Billed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(scheduleIData.total_billed)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Net Over/(Under)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${scheduleIData.total_over_under >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(scheduleIData.total_over_under)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Contracts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">{scheduleIData.over_billed_count}</div>
                  <div className="text-xs text-gray-500">Over</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600">{scheduleIData.under_billed_count}</div>
                  <div className="text-xs text-gray-500">Under</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-600">{scheduleIData.contract_count}</div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Data Table */}
      {scheduleIData && (
        <>
          {/* Filters */}
          <div className="pro-card" style={{ marginBottom: '1.5rem' }}>
            <div className="pro-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="pro-card-title">Filters & Options</h3>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.825rem' }}>
                <input type="checkbox" checked={hasDataOnly} onChange={(e) => { setHasDataOnly(e.target.checked); setTimeout(applyFilters, 0); }} />
                Hide zero-cost contracts
              </label>
            </div>
            <div className="pro-card-content">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto auto', gap: '1rem', alignItems: 'end' }}>
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                    Search Contract:
                  </label>
                  <input
                    type="text"
                    value={contractSearch}
                    onChange={(e) => setContractSearch(e.target.value)}
                    placeholder="e.g. 11335"
                    className="pro-input"
                    style={{ fontSize: '0.875rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                    Sort By:
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="pro-input"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <option value="internal_contract_number">Contract Number</option>
                    <option value="total_cumulative_settled_or_claimed">Total Claimed</option>
                    <option value="cumulative_billed_to_date">Total Billed</option>
                    <option value="cumulative_over_under_billing">Over/Under Billing</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem', display: 'block' }}>
                    View Limit:
                  </label>
                  <select
                    value={viewLimit}
                    onChange={(e) => setViewLimit(parseInt(e.target.value))}
                    className="pro-input"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <option value={200}>200 rows</option>
                    <option value={500}>500 rows</option>
                    <option value={1000}>1000 rows</option>
                    <option value={2000}>2000 rows</option>
                  </select>
                </div>

                <button
                  className="pro-btn pro-btn-primary"
                  onClick={applyFilters}
                  style={{ fontSize: '0.875rem', padding: '0.625rem 1.25rem' }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>

          {/* Schedule I Table */}
          <div className="pro-card">
            <div className="pro-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="pro-card-title">
                Schedule I Data
                {scheduleIData.filtered_count !== undefined && (
                  <span style={{ fontWeight: '400', marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                    ({scheduleIData.filtered_count} of {scheduleIData.contract_count} contracts)
                  </span>
                )}
              </h3>
              <button
                className="pro-btn pro-btn-secondary"
                onClick={handleExport}
                style={{ fontSize: '0.875rem', padding: '0.625rem 1.25rem' }}
              >
                <Download style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                Export to Excel
              </button>
            </div>
            <div
              className="pro-card-content"
              style={{
                padding: 0,
                overflow: 'auto',
                maxHeight: 'calc(100vh - 500px)'
              }}
            >
              <table className="financial-table" style={{ minWidth: 'max-content', position: 'relative', borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 100 }}>
                  <tr>
                    <th
                      style={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 101,
                        background: 'var(--gray-800)',
                        color: 'var(--white)',
                        padding: '0.875rem 0.75rem',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        textAlign: 'left',
                        borderBottom: '2px solid var(--gray-600)',
                        minWidth: '60px'
                      }}
                    >
                      #
                    </th>
                    {scheduleIColumns.map((col) => {
                      const cat = col.category || headerCategory(col.label);
                      return (
                        <th
                          key={col.key}
                          style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 100,
                            background: headerBg(cat),
                            color: 'var(--white)',
                            padding: '0.875rem 0.75rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textAlign: 'left',
                            borderBottom: '2px solid var(--gray-600)',
                            minWidth: '120px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {col.label}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {scheduleIData.contracts?.slice(0, viewLimit).map((contract, idx) => (
                    <tr
                      key={idx}
                      style={{
                        background: idx % 2 === 0 ? 'var(--white)' : 'var(--gray-50)',
                        borderBottom: '1px solid var(--gray-200)'
                      }}
                    >
                      <td
                        style={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 50,
                          background: idx % 2 === 0 ? 'var(--white)' : 'var(--gray-50)',
                          padding: '0.75rem',
                          fontSize: '0.8125rem',
                          fontWeight: '500',
                          borderRight: '1px solid var(--gray-200)'
                        }}
                      >
                        {idx + 1}
                      </td>
                      {scheduleIColumns.map((col) => {
                        const value = getColumnValue(contract, col);
                        const cat = col.category || headerCategory(col.label);
                        // Columns that should be formatted as currency (numeric)
                        // Exclude voucher_no and date_costs_billed_through which are text/date fields
                        const isNumeric = ['costs', 'labor', 'fringe', 'overhead', 'odc', 'indirect', 'unbillable', 'billing', 'grand_total', 'adjustment', 'total'].includes(cat) 
                          && col.key !== 'voucher_no' 
                          && col.key !== 'date_costs_billed_through';
                        
                        // Check if this is a date column
                        const isDate = col.key === 'date_costs_billed_through';
                        
                        // Format the value based on type
                        let displayValue;
                        if (isNumeric) {
                          displayValue = formatCurrency(value);
                        } else if (isDate) {
                          displayValue = formatDate(value);
                        } else {
                          displayValue = value || '—';
                        }
                        
                        return (
                          <td
                            key={col.key}
                            style={{
                              padding: '0.75rem',
                              fontSize: '0.8125rem',
                              textAlign: isNumeric ? 'right' : 'left',
                              whiteSpace: 'nowrap',
                              color: col.key === 'cumulative_over_under_billing' && value < 0 ? 'var(--error-red)' : 'inherit'
                            }}
                          >
                            {displayValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {loading && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                  <RefreshCw style={{ width: '2rem', height: '2rem', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
                  Loading Schedule I data...
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ScheduleIPage;
